import React from 'react';
import * as Yup from 'yup';
import { Form, FormHandlers, FormProps } from '@doveit/bricks';
import { omit } from 'lodash';
import { ShootingType, VirtualTourType } from '../../../domain/types';
import { upsertShootingValidationSchema } from './UpsertShootingForm.schema';
import { asSelectOptions } from '../../../select-options/utils/asSelectOptions';
import { shootingTypeLabels, virtualTourTypeLabels } from '../../../labels';
import PhotographerAutoComplete, { PhotographerAutoCompleteProps } from '../../containers/photographer-auto-complete/PhotographerAutoComplete';
import { INPUT_PLACEHOLDERS } from '../../../constants';
import { NO_VIRTUAL_TOUR } from '../../constants';
import { PhotographerSearchResult } from '../../../providers/api/dtos';

export interface UpsertShootingFormModel {
  type: ShootingType | '',
  photographer?: PhotographerSearchResult,
  date: string,
  time: string,
  virtualTourType: VirtualTourType | typeof NO_VIRTUAL_TOUR | '',
  photoPrice: string,
  extraServicesPrice: string,
  expensesRefund: string,
  deliveryDate: string,
  notes: string
}

export interface UpsertShootingFormProps {
  initialValues?: Partial<UpsertShootingFormModel>,
  hiddenFields?: Partial<Record<keyof UpsertShootingFormModel, boolean>>,
  disabledFields?: Partial<Record<keyof UpsertShootingFormModel, boolean>>,
  innerRef?: React.MutableRefObject<FormHandlers>,
  validationSchema?: Yup.AnySchema | ReturnType<typeof upsertShootingValidationSchema>,
  size?: 'S' | 'M',
  loading?: FormProps<UpsertShootingFormModel>['loading'],
  onSubmit: FormProps<UpsertShootingFormModel>['onSubmit'],
}

export const UPSERT_SHOOTING_FORM_DEFAULT_VALUES: UpsertShootingFormModel = {
  type: '',
  date: '',
  time: '',
  virtualTourType: '',
  photoPrice: '',
  extraServicesPrice: '',
  expensesRefund: '',
  deliveryDate: '',
  notes: '',
};

const UpsertShootingForm: React.FC<UpsertShootingFormProps> = ({
  initialValues,
  hiddenFields,
  disabledFields,
  validationSchema,
  size = 'M',
  ...rest
}) => {
  const mergedInitialValues: UpsertShootingFormModel = React.useMemo(() => ({
    ...UPSERT_SHOOTING_FORM_DEFAULT_VALUES,
    ...initialValues,
  }), [initialValues]);

  return (
    <Form
      initialValues={mergedInitialValues}
      validationSchema={typeof validationSchema === 'function'
        ? validationSchema(hiddenFields, disabledFields)
        : validationSchema}
      {...rest}
    >
      {({ values, setFieldValue }) => {
        const onTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
          if (values.type === 'DIRECT' && e.target.value !== 'DIRECT') {
            setFieldValue('photographer', UPSERT_SHOOTING_FORM_DEFAULT_VALUES.photographer);
            setFieldValue('photoPrice', UPSERT_SHOOTING_FORM_DEFAULT_VALUES.photoPrice);
          }
        };

        const onPhotographerSelected: PhotographerAutoCompleteProps['onSuggestionSelected'] = (photographer) => {
          setFieldValue('photographer', photographer || UPSERT_SHOOTING_FORM_DEFAULT_VALUES.photographer);
        };

        return (
          <>
            {!hiddenFields?.type && (
              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.Select
                  size={size}
                  name="type"
                  label="Tipologia"
                  aria-label="Campo per specificare la tipologia del fotografo"
                  options={[
                    ...asSelectOptions(
                      omit(shootingTypeLabels, [ShootingType.PARTNER]),
                      {
                        value: '',
                        label: 'Seleziona',
                        disabled: true,
                      },
                    ),
                    {
                      label: 'Partner',
                      value: ShootingType.PARTNER,
                      disabled: true,
                    },
                  ]}
                  onChange={onTypeChange}
                  disabled={disabledFields?.type}
                  hasErrorTooltip
                  required
                />
              </Form.Item>
            )}

            {!hiddenFields?.photographer && (
              <Form.Item size={{ MD: 1 / 2 }}>
                {values.type === 'DIRECT' && (
                  <PhotographerAutoComplete
                    size={size}
                    name="photographer"
                    label="Nome fotografo"
                    aria-label="Campo per specificare il nome del fotografo"
                    placeholder={INPUT_PLACEHOLDERS.CONTACT_NAME_SURNAME}
                    initialPhotographer={values.photographer}
                    disabled={disabledFields?.photographer}
                    onSuggestionSelected={onPhotographerSelected}
                    required
                  />
                )}
              </Form.Item>
            )}

            {!hiddenFields?.date && (
              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.Input
                  size={size}
                  name="date"
                  label="Data shooting"
                  type="date"
                  aria-label="Campo per specificare la data dello shooting"
                  disabled={disabledFields?.date}
                  hasErrorTooltip
                  required
                />
              </Form.Item>
            )}

            {!hiddenFields?.time && (
              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.Input
                  size={size}
                  name="time"
                  label="Ora shooting"
                  type="time"
                  aria-label="Campo per specificare l'ora dello shooting"
                  disabled={disabledFields?.time}
                  hasErrorTooltip
                  required
                />
              </Form.Item>
            )}

            {!hiddenFields?.virtualTourType && (
              <Form.Fieldset legend="Extra">
                <Form.Item size={{ MD: 1 / 2 }}>
                  <Form.Select
                    size={size}
                    name="virtualTourType"
                    label="Virtual tour"
                    aria-label="Campo per specificare la tipologia di virtual tour"
                    options={[
                      {
                        value: '',
                        label: 'Seleziona',
                        disabled: true,
                      },
                      {
                        value: NO_VIRTUAL_TOUR,
                        label: 'Nessuno',
                      },
                      ...asSelectOptions(virtualTourTypeLabels, false),
                    ]}
                    disabled={disabledFields?.virtualTourType}
                    hasErrorTooltip
                    required
                  />
                </Form.Item>
              </Form.Fieldset>
            )}

            {!hiddenFields?.photoPrice && values.type === ShootingType.DIRECT && (
              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.DecimalPriceField
                  size={size}
                  name="photoPrice"
                  label="Costo foto"
                  aria-label="Campo per specificare il costo delle foto"
                  disabled={disabledFields?.photoPrice}
                />
              </Form.Item>
            )}

            {!hiddenFields?.extraServicesPrice && values.type === ShootingType.DIRECT && (
              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.DecimalPriceField
                  size={size}
                  name="extraServicesPrice"
                  label="Costo extra"
                  aria-label="Campo per specificare il costo dei servizi extra"
                  disabled={disabledFields?.extraServicesPrice}
                />
              </Form.Item>
            )}

            {!hiddenFields?.expensesRefund && values.type === ShootingType.DIRECT && (
              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.DecimalPriceField
                  size={size}
                  name="expensesRefund"
                  label="Rimborso spese"
                  aria-label="Campo per specificare il costo del rimborso spese"
                  disabled={disabledFields?.expensesRefund}
                />
              </Form.Item>
            )}

            {!hiddenFields?.deliveryDate && (
              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.Input
                  size={size}
                  name="deliveryDate"
                  label="Data consegna"
                  type="date"
                  hasErrorTooltip
                  aria-label="Campo per specificare la data di consegna dello shooting"
                  disabled={disabledFields?.deliveryDate}
                />
              </Form.Item>
            )}

            {!hiddenFields?.notes && (
              <Form.Item>
                <Form.TextArea
                  size={size}
                  name="notes"
                  aria-label="Campo per specificare le note"
                  label="Note"
                  rows={3}
                  style={{ resize: 'vertical' }}
                  disabled={disabledFields?.notes}
                />
              </Form.Item>
            )}
          </>
        );
      }}
    </Form>
  );
};

export default UpsertShootingForm;
